import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { TranscriptBuffer, SuggestionBuffer, TranscriptChunk, CoachingSuggestion } from '@/lib/ringBuffer';

interface RealtimeBufferOptions {
  transcriptCap?: number;
  suggestionCap?: number;
  batchMs?: number;
  onError?: (error: Error) => void;
}

interface RealtimeBufferState {
  transcripts: TranscriptChunk[];
  suggestions: CoachingSuggestion[];
  isConnected: boolean;
  error: Error | null;
}

const DEFAULT_OPTIONS: Required<RealtimeBufferOptions> = {
  transcriptCap: 400,
  suggestionCap: 100,
  batchMs: 150,
  onError: console.error
};

/**
 * Custom hook for managing real-time transcripts and suggestions with efficient buffering
 */
export function useRealtimeBuffer(
  callSids: string[],
  options: RealtimeBufferOptions = {}
): RealtimeBufferState & { clearBuffers: () => void } {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  const [state, setState] = useState<RealtimeBufferState>({
    transcripts: [],
    suggestions: [],
    isConnected: false,
    error: null
  });

  // Refs for buffers and channels
  const transcriptBuffer = useRef(new TranscriptBuffer(opts.transcriptCap));
  const suggestionBuffer = useRef(new SuggestionBuffer(opts.suggestionCap));
  const channels = useRef<any[]>([]);
  const flushTimeout = useRef<number>();
  const retryTimeouts = useRef<number[]>([]);
  const mountedRef = useRef(true);

  // Batch updates to reduce renders
  const scheduleFlush = useCallback(() => {
    if (flushTimeout.current) return;
    flushTimeout.current = window.setTimeout(() => {
      if (!mountedRef.current) return;
      flushTimeout.current = undefined;
      setState(prev => ({
        ...prev,
        transcripts: transcriptBuffer.current.getItems(),
        suggestions: suggestionBuffer.current.getItems()
      }));
    }, opts.batchMs);
  }, [opts.batchMs]);

  // Handle channel subscription with backoff
  const subscribeToChannel = useCallback(async (
    sid: string,
    attempt: number = 0
  ) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token || !mountedRef.current) return;

      // Set auth token for realtime
      await supabase.realtime.setAuth(session.access_token);

      // Create broadcast channel for real-time updates
      // IMPORTANT: self: true allows receiving broadcasts from the same client (agent audio)
      const channel = supabase
        .channel(`call-${sid}`, {
          config: {
            broadcast: { self: true }
          }
        })
        .on('broadcast', { event: 'transcript' }, payload => {
          try {
            const data = (payload as any).payload || payload;
            if (!data?.text) return;

            const chunk: TranscriptChunk = {
              id: data.id || `${sid}-${Date.now()}`,
              callSid: data.callSid || data.call_sid || sid,
              speaker: (data.speaker === 'agent' || data.speaker === 'customer') 
                ? data.speaker 
                : 'customer',
              text: data.text,
              timestamp: data.timestamp || new Date().toISOString(),
              final: !!data.final,
              confidence: typeof data.confidence === 'number' ? data.confidence : undefined
            };

            transcriptBuffer.current.upsert(chunk);
            scheduleFlush();
          } catch (err) {
            console.error('[Realtime] Error handling transcript:', err);
          }
        })
        .on('broadcast', { event: 'coaching' }, payload => {
          try {
            const data = (payload as any).payload || payload;
            if (!Array.isArray(data?.suggestions)) return;

            const newSuggestions = data.suggestions
              .filter((s: any) => s?.message)
              .map((s: any) => ({
                id: s.id || `${sid}-${Date.now()}-${Math.random()}`,
                type: s.type || 'info',
                severity: s.severity || 'info',
                title: s.title || '',
                message: s.message,
                suggestedScript: s.suggestedScript,
                timestamp: s.timestamp || Date.now(),
                source: s.source,
                responseTime: s.responseTime
              }));

            if (newSuggestions.length > 0) {
              suggestionBuffer.current.upsertMany(newSuggestions);
              scheduleFlush();
            }
          } catch (err) {
            console.error('[Realtime] Error handling coaching:', err);
          }
        })
        .subscribe(status => {
          if (!mountedRef.current) return;

          console.log(`[Realtime] Channel ${sid} status:`, status);

          if (status === 'SUBSCRIBED') {
            console.log(`[Realtime] Successfully connected to ${sid}`);
            setState(prev => ({ ...prev, isConnected: true, error: null }));
          } else if (status === 'CHANNEL_ERROR') {
            console.error(`[Realtime] Channel error for ${sid}:`, status);
            setState(prev => ({
              ...prev,
              isConnected: false,
              error: new Error(`Channel error for ${sid}`)
            }));
          } else if (status === 'TIMED_OUT') {
            console.warn(`[Realtime] Connection timed out for ${sid}, retrying...`);
            // Clear any existing retry
            const existingTimeout = retryTimeouts.current[attempt];
            if (existingTimeout) {
              clearTimeout(existingTimeout);
              retryTimeouts.current[attempt] = 0;
            }

            // Exponential backoff for retries
            if (attempt < 5) {
              const timeout = Math.min(1000 * Math.pow(2, attempt), 30000);
              retryTimeouts.current[attempt] = window.setTimeout(() => {
                if (mountedRef.current) {
                  console.log(`[Realtime] Retrying connection for ${sid}, attempt ${attempt + 1}`);
                  subscribeToChannel(sid, attempt + 1);
                }
              }, timeout);
            } else {
              setState(prev => ({
                ...prev,
                isConnected: false,
                error: new Error(`Failed to connect after ${attempt} attempts`)
              }));
            }
          } else if (status === 'CLOSED') {
            console.log(`[Realtime] Channel closed for ${sid}`);
            setState(prev => ({ ...prev, isConnected: false, error: null }));
          } else {
            console.log(`[Realtime] Other status for ${sid}:`, status);
          }
        });

      channels.current.push(channel);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setState(prev => ({ ...prev, error }));
      opts.onError(error);
    }
  }, [opts.onError, scheduleFlush]);

  // Set up subscriptions
  useEffect(() => {
    mountedRef.current = true;

    // Clear existing subscriptions
    channels.current.forEach(ch => {
      try {
        supabase.removeChannel(ch);
      } catch {}
    });
    channels.current = [];

    // Clear retry timeouts
    retryTimeouts.current.forEach(timeout => {
      if (timeout) clearTimeout(timeout);
    });
    retryTimeouts.current = [];

    // Subscribe to new call SIDs
    callSids.forEach(sid => {
      if (sid) subscribeToChannel(sid);
    });

    return () => {
      mountedRef.current = false;
      
      // Clear flush timeout
      if (flushTimeout.current) {
        clearTimeout(flushTimeout.current);
        flushTimeout.current = undefined;
      }

      // Clear retry timeouts
      retryTimeouts.current.forEach(timeout => {
        if (timeout) clearTimeout(timeout);
      });

      // Clean up channels
      channels.current.forEach(ch => {
        try {
          supabase.removeChannel(ch);
        } catch {}
      });
    };
  }, [callSids, subscribeToChannel]);

  const clearBuffers = useCallback(() => {
    transcriptBuffer.current.clear();
    suggestionBuffer.current.clear();
    setState(prev => ({
      ...prev,
      transcripts: [],
      suggestions: []
    }));
  }, []);

  return {
    ...state,
    clearBuffers
  };
}
